/**
 * Shop grid — the storefront product experience.
 *
 * A responsive grid of photoreal product cards (Blender Cycles renders).
 * Tap a card → an interactive 3D + AR modal (Google <model-viewer>, which
 * renders the .glb with orbit controls and native AR). Photos sell; 3D
 * delights on intent. No heavy Three.js scene in the store browse view.
 *
 * Mounts into #app for storefront routes. Store chrome (bar/countdown/
 * manifesto/footer) is provided separately by store.js.
 */
import { el, isDarkHex } from './core/utils.js';

let mvLoaded = false;
async function ensureModelViewer() {
  if (mvLoaded) return;
  mvLoaded = true;
  await import(/* @vite-ignore */ 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js');
}

function buildCard(p, i) {
  const media = p.image
    ? el('div', { className: 'shop-card__media' },
        el('img', { src: p.image, alt: p.name, loading: 'lazy', draggable: 'false' }),
        p.model ? el('span', { className: 'shop-card__3d' }, '◷ 3D / AR') : null,
      )
    : el('div', { className: 'shop-card__media shop-card__media--blank' });

  const variantDots = (p.variants && p.variants.length > 1)
    ? el('div', { className: 'shop-card__variants' },
        ...p.variants.map((v) => el('span', { className: 'shop-card__dot', style: { background: v.colorA }, title: v.name || v.sku })))
    : null;

  const buy = p.stripeLink
    ? el('a', { className: 'shop-card__buy', href: p.stripeLink, target: '_blank', rel: 'noopener' }, 'Add — ' + p.price)
    : el('button', { className: 'shop-card__buy', type: 'button' }, 'Add — ' + p.price);

  return el('article', {
    className: 'shop-card',
    dataset: { index: String(i), model: p.model || '', image: p.image || '', name: p.name, bg: p.colorA },
    style: { '--card': p.colorA },
  },
    media,
    el('div', { className: 'shop-card__info' },
      el('span', { className: 'shop-card__tag' }, p.tag || ''),
      el('h3', { className: 'shop-card__name' }, p.name),
      variantDots,
      el('div', { className: 'shop-card__row' },
        el('span', { className: 'shop-card__price' }, p.price),
        buy,
      ),
    ),
  );
}

function openViewer(card) {
  const model = card.dataset.model;
  const poster = card.dataset.image;
  const bg = card.dataset.bg;
  let modal = document.querySelector('.mv-modal');
  if (!modal) {
    modal = el('div', { className: 'mv-modal' });
    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.dataset.close === 'true') modal.classList.remove('is-open');
    });
    document.body.appendChild(modal);
  }
  modal.replaceChildren();
  modal.appendChild(el('button', { className: 'mv-close', dataset: { close: 'true' }, 'aria-label': 'Close' }, '✕'));
  const viewer = document.createElement('model-viewer');
  viewer.setAttribute('src', model);
  if (poster) viewer.setAttribute('poster', poster);
  viewer.setAttribute('camera-controls', '');
  viewer.setAttribute('auto-rotate', '');
  viewer.setAttribute('ar', '');
  viewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
  viewer.setAttribute('shadow-intensity', '1');
  viewer.setAttribute('exposure', '1');
  viewer.setAttribute('environment-image', 'neutral');
  viewer.setAttribute('camera-orbit', '0deg 80deg 105%');
  viewer.style.backgroundColor = bg || '#111';
  modal.appendChild(viewer);
  const hint = el('p', { className: 'mv-hint' }, 'Drag to rotate · tap ⛶ for AR');
  modal.appendChild(hint);
  modal.classList.add('is-open');
}

export function mountShop(host, brand, products) {
  host.classList.add('tpl-shop');

  const grid = el('div', { className: 'shop-grid' });
  for (let i = 0; i < products.length; i++) grid.appendChild(buildCard(products[i], i));

  const main = el('main', { className: 'shop-main' },
    el('header', { className: 'shop-head' },
      el('p', { className: 'shop-head__eyebrow' }, (brand.drop && brand.drop.label) || 'The Collection'),
      el('h2', { className: 'shop-head__title' }, brand.tagline || ''),
    ),
    grid,
  );
  host.appendChild(main);

  // Tap card (not the buy button) → open the 3D/AR viewer.
  grid.addEventListener('click', async (e) => {
    if (e.target.closest('.shop-card__buy')) return;       // let buy work
    const card = e.target.closest('.shop-card');
    if (!card || !card.dataset.model) return;
    await ensureModelViewer();
    openViewer(card);
  });
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') document.querySelector('.mv-modal')?.classList.remove('is-open');
  });

  return {
    destroy() {
      host.classList.remove('tpl-shop');
      main.remove();
    },
  };
}
